import React, { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import {
  Box,
  Drawer,
  CssBaseline,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Work as WorkIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  MonetizationOn as RevenueIcon,
  AdminPanelSettings as AdminManagementIcon,
  RateReview as ReviewIcon,
  Security as AuditIcon,
  Assignment as ApplicationIcon,
  RocketLaunch as BoostIcon,
  Campaign as AdsIcon,
  
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const drawerWidth = 240;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3, 0),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
  }),
);

const AppBarStyled = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

const AdminLayout = () => {
  const theme = useTheme();
  const [open, setOpen] = useState(true);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const { currentUser, currentAdmin, isAdmin, logout } = useAuth();
  const navigate = useNavigate();

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to log out', error);
    }
    handleCloseUserMenu();
  };

  // Debug logs only in development to avoid leaking sensitive context
  if (process.env.NODE_ENV !== 'production') {
    console.log('=== ADMIN LAYOUT DEBUG ===');
    console.log('Current user:', currentUser);
    console.log('Current user role:', currentUser?.role);
    console.log('Current user userType:', currentUser?.userType);
    console.log('Current user primaryRole:', currentUser?.primaryRole);
    console.log('Current admin:', currentAdmin);
    console.log('Is admin:', isAdmin);
    console.log('========================');
  }

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin' },
    { text: 'Users', icon: <PeopleIcon />, path: '/admin/users' },
    { text: 'Jobs', icon: <WorkIcon />, path: '/admin/jobs' },
    { text: 'All Job Applications', icon: <ApplicationIcon />, path: '/admin/job-applications' },
    { text: 'Revenue', icon: <RevenueIcon />, path: '/admin/revenue' },
    { 
      text: 'Communication', 
      icon: <PeopleIcon />, 
      path: '/admin/communications',
      roles: ['super_admin', 'admin']
    },
    { 
      text: 'SMS', 
      icon: <PeopleIcon />, 
      path: '/admin/sms',
      roles: ['super_admin', 'admin']
    },
    { 
      text: 'Boost Requests', 
      icon: <BoostIcon />, 
      path: '/admin/boost-requests',
      roles: ['super_admin', 'admin']
    },
    { 
      text: 'Job Boosts', 
      icon: <BoostIcon />, 
      path: '/admin/job-boosts',
      roles: ['super_admin', 'admin']
    },
    { 
      text: 'Admin Management', 
      icon: <AdminManagementIcon />, 
      path: '/admin/admin-management',
      roles: ['super_admin', 'admin'] // Only visible to super_admin and admin roles
    },
    { 
      text: 'Ads Management', 
      icon: <AdsIcon />, 
      path: '/admin/ads',
      roles: ['super_admin', 'admin']
    },
    { 
      text: 'Review Management', 
      icon: <ReviewIcon />, 
      path: '/admin/review-management',
      roles: ['super_admin', 'admin', 'review_officer'] // Only visible to authorized roles
    },
    { 
      text: 'Audit Logs', 
      icon: <AuditIcon />, 
      path: '/admin/audit-logs',
      roles: ['super_admin', 'admin'] // Only visible to super_admin and admin roles
    },
    { 
      text: 'Contact Requests',
      icon: <PeopleIcon />,
      path: '/admin/contact-requests',
      roles: ['super_admin', 'admin']
    },
    { text: 'Settings', icon: <SettingsIcon />, path: '/admin/settings' },
  ];

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBarStyled position="fixed" open={open}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{ mr: 2, ...(open && { display: 'none' }) }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Admin Dashboard
          </Typography>
          <Box sx={{ flexGrow: 0 }}>
            <Tooltip title="Open settings">
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                <Avatar alt={currentUser?.displayName} src="/static/images/avatar/2.jpg" />
              </IconButton>
            </Tooltip>
            <Menu
              sx={{ mt: '45px' }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              <MenuItem onClick={handleCloseUserMenu}>
                <Typography textAlign="center">Profile</Typography>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <Typography textAlign="center">Logout</Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBarStyled>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        <DrawerHeader>
          <Typography variant="h6" sx={{ flexGrow: 1, ml: 2 }}>
            Job Portal
          </Typography>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          {menuItems
            .filter(item => {
              if (!item.roles) return true; // Show items without role restrictions
              
              // For super admin, show everything (GodMode)
              if (currentAdmin?.role === 'super_admin') {
                console.log('SUPER ADMIN DETECTED - Showing all menus');
                return true;
              }
              
              // Check multiple possible role properties
              const userRole = currentUser?.role || currentUser?.userType || currentUser?.primaryRole;
              const adminRole = currentAdmin?.role;
              
              console.log('Filtering menu item:', item.text, 'Required roles:', item.roles, 'User role:', userRole, 'Admin role:', adminRole);
              
              // Check both currentUser and currentAdmin for role
              return (userRole && item.roles.includes(userRole)) || (adminRole && item.roles.includes(adminRole));
            })
            .map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton component={RouterLink} to={item.path}>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
      <Main open={open}>
        <DrawerHeader />
        <Outlet />
      </Main>
    </Box>
  );
};

export default AdminLayout;